import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { askReporting, getReporting } from "../../api/reporting";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import ReportCharts from "./ReportCharts";
import Recommendations from "./Recommendations";

const localDateKey = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const sections = ["overview", "attendance", "productivity", "points", "leave", "payroll", "wallet", "requests", "performance"];
const exampleQuestions = [
  "How many departments do we have?",
  "How many employees do we have?",
  "Show my biodata",
  "Show me my report for this month",
  "What is the attendance summary for this month?",
  "Who has the highest task completion rate this month?",
  "Who earned the most points this month?",
  "What is the payroll summary for this month?",
];
const label = (key) => `${key || ""}`.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase());
const display = (key, value) => {
  if (value == null) return "—";
  if (typeof value !== "number") return String(value);
  if (key.endsWith("Percent")) return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  if (key.startsWith("payroll") || key.startsWith("walletCash")) return `₦${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return value.toLocaleString();
};
const message = (error) => error?.response?.status === 404
  ? "Reporting is not available on this backend yet. Deploy the reporting endpoints to enable this view."
  : error?.response?.data?.message || "Unable to load reporting data. Please try again.";

export default function Reporting() {
  const { pathname } = useLocation();
  const intelligence = pathname.endsWith("/intelligence");
  const recommendations = pathname.endsWith("/recommendations");
  const [recommendationsVisited, setRecommendationsVisited] = useState(recommendations);
  const [today, setToday] = useState(localDateKey);
  const [from, setFrom] = useState(() => `${localDateKey().slice(0, 7)}-01`);
  const [to, setTo] = useState(localDateKey);
  const [section, setSection] = useState("overview");
  const [departmentId, setDepartmentId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [report, setReport] = useState(null);
  const [displayedKey, setDisplayedKey] = useState("");
  const reportCache = useRef(new Map());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);
  const askVersion = useRef(0);
  const [page, setPage] = useState(1);
  const [reportView, setReportView] = useState("table");
  const params = { from, to, section, ...(departmentId ? { departmentId } : {}), ...(employeeId ? { employeeId } : {}) };
  const reportKey = JSON.stringify(params);

  useEffect(() => {
    const timer = window.setInterval(() => setToday(localDateKey()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => { if (recommendations) setRecommendationsVisited(true); }, [recommendations]);

  useEffect(() => {
    askVersion.current += 1;
    setAnswer(null);
    setAsking(false);
    setError("");
    return () => { askVersion.current += 1; };
  }, [from, to]);

  useEffect(() => {
    askVersion.current += 1;
    setAsking(false);
  }, [intelligence]);

  useEffect(() => setPage(1), [from, to, section, departmentId, employeeId]);

  useEffect(() => {
    if (intelligence || recommendations) return undefined;
    if (from > to) { setReport(null); setDisplayedKey(""); setError("Start date must be on or before the end date."); setLoading(false); return undefined; }
    const cached = reportCache.current.get(reportKey);
    if (cached) {
      setReport(cached);
      setDisplayedKey(reportKey);
      setLoading(false);
      setError("");
      return undefined;
    }
    let active = true;
    setReport(null);
    setDisplayedKey("");
    setLoading(true);
    setError("");
    getReporting(params).then((result) => { if (active) { reportCache.current.set(reportKey, result); setReport(result); setDisplayedKey(reportKey); } })
      .catch((err) => { if (active) setError(message(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // Filters are intentionally the only dependencies; params is reconstructed on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intelligence, recommendations, from, to, section, departmentId, employeeId, reportKey, retry]);

  const submitQuestion = async (value) => {
    const selectedQuestion = value.trim();
    if (!selectedQuestion || asking || from > to) return;
    const version = ++askVersion.current;
    setQuestion(selectedQuestion);
    setAsking(true); setError("");
    try {
      const result = await askReporting(selectedQuestion, { from, to });
      if (version === askVersion.current) setAnswer(result);
    } catch (err) {
      if (version === askVersion.current) setError(message(err));
    } finally {
      if (version === askVersion.current) setAsking(false);
    }
  };
  const ask = (event) => {
    event.preventDefault();
    submitQuestion(question);
  };

  const metrics = report?.metrics && typeof report.metrics === "object" ? Object.entries(report.metrics) : [];
  const rows = Array.isArray(report?.employees) ? report.employees : [];
  const columns = rows.length ? Object.keys(rows[0]).filter((key) => key !== "id") : [];
  const pageCount = Math.ceil(rows.length / 25);
  const visibleRows = rows.slice((page - 1) * 25, page * 25);
  useEffect(() => {
    if (page > Math.max(1, pageCount)) setPage(Math.max(1, pageCount));
  }, [page, pageCount]);
  const exportCsv = () => {
    const escape = (value) => {
      const text = typeof value === "string" && /^[\s]*[=+\-@]/.test(value) ? `'${value}` : String(value ?? "");
      return `"${text.replace(/"/g, '""')}"`;
    };
    const csv = [columns.map(escape).join(","), ...rows.map((row) => columns.map((key) => escape(row[key])).join(","))].join("\r\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `cehr-${section}-${from}-${to}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  return <main className="min-h-screen min-w-0 bg-slate-50 px-3 py-4 font-Outfit text-slate-900 sm:px-5 sm:py-6 md:px-8 md:py-8">
    <div className="w-full min-w-0 space-y-5 sm:space-y-6">
      <header><h1 className="text-2xl font-semibold">Reports & Intelligence</h1><p className="text-sm text-slate-500">Insights are limited to the people and data you are authorized to see.</p></header>
      {recommendationsVisited && <div hidden={!recommendations}><Recommendations active={recommendations} /></div>}
      {!recommendations && (intelligence ? <section className="max-w-3xl min-w-0 rounded-xl border bg-white p-4 shadow-sm sm:p-6">
        <h2 className="text-lg font-semibold">Ask about your organization</h2>
        <p className="mt-1 text-sm text-slate-500">Ask about department or staff counts, an authorized employee's biodata, your own summary, or attendance, productivity and points rankings. Answers use authorized records only.</p>
        <p className="mt-1 text-xs text-slate-500">The selected dates apply unless you ask about this or last month, this or last week, today, or yesterday.</p>
        <form onSubmit={ask} className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4"><label className="min-w-0 text-sm">From <input aria-label="From" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1 block w-full min-w-0 rounded border p-2 sm:mt-0 sm:ml-2 sm:inline-block sm:w-auto" /></label><label className="min-w-0 text-sm">To <input aria-label="To" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} className="mt-1 block w-full min-w-0 rounded border p-2 sm:mt-0 sm:ml-2 sm:inline-block sm:w-auto" /></label></div>
          {from > to && <p role="alert" className="text-sm text-red-700">Start date must be on or before the end date.</p>}
          <label className="block text-sm font-medium" htmlFor="report-question">Your question</label>
          <textarea id="report-question" maxLength={1000} required value={question} onChange={(e) => setQuestion(e.target.value)} rows={3} className="w-full rounded-lg border p-3" placeholder="What would you like to know?" />
          <div aria-label="Example questions">
            <p className="mb-2 text-sm font-medium text-slate-700">Try a question</p>
            <div className="flex flex-wrap gap-2">{exampleQuestions.map((example) => <button key={example} type="button" disabled={asking || from > to} onClick={() => submitQuestion(example)} className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-left text-sm text-blue-800 transition-colors hover:border-blue-400 hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50">{example}</button>)}</div>
          </div>
          <button disabled={asking || from > to} className="rounded-lg bg-blue-700 px-5 py-2 text-white disabled:opacity-50">{asking ? "Thinking…" : "Ask"}</button>
        </form>
        {answer && <div aria-live="polite" className="mt-5 rounded-lg bg-blue-50 p-4 text-sm whitespace-pre-wrap">
          {answer.range && <p className="mb-2 text-xs font-semibold text-blue-800">Period: {answer.range.from} to {answer.range.to}</p>}
          {typeof answer.answer === "string" ? answer.answer : "No answer available for this question."}
        </div>}
      </section> : <>
        <nav className="flex gap-2 border-b border-slate-200" aria-label="Report views">
          {[["table", "Report View"], ["charts", "Chart View"]].map(([view, name]) => <button key={view} type="button" aria-current={reportView === view ? "page" : undefined} onClick={() => setReportView(view)} className={`px-4 py-2 text-sm ${reportView === view ? "border-b-2 border-blue-600 font-semibold text-blue-700" : "text-slate-600"}`}>{name}</button>)}
        </nav>
        <section className="grid grid-cols-2 items-end gap-3 rounded-xl border bg-white p-3 shadow-sm sm:flex sm:flex-wrap sm:gap-4 sm:p-5" aria-label="Report filters">
          <label className="min-w-0 text-sm">From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1 block w-full min-w-0 rounded border p-2 sm:w-auto" /></label>
          <label className="min-w-0 text-sm">To <input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} className="mt-1 block w-full min-w-0 rounded border p-2 sm:w-auto" /></label>
          <label className="col-span-2 min-w-0 text-sm sm:col-span-1">Section <select value={section} onChange={(e) => setSection(e.target.value)} className="mt-1 block w-full min-w-0 rounded border p-2 sm:w-auto">{sections.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></label>
          {(report?.departments || []).length > 0 && <label className="col-span-2 min-w-0 text-sm sm:col-span-1">Department <select value={departmentId} onChange={(e) => { setDepartmentId(e.target.value); setEmployeeId(""); }} className="mt-1 block w-full min-w-0 rounded border p-2 sm:max-w-64"><option value="">All authorized departments</option>{report.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></label>}
          {(report?.people || []).length > 1 && <label className="col-span-2 min-w-0 text-sm sm:col-span-1">Person <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="mt-1 block w-full min-w-0 rounded border p-2 sm:max-w-64"><option value="">All authorized people</option>{report.people.filter((p) => !departmentId || p.departmentIds?.includes(departmentId)).map((p) => <option key={p.id} value={p.id}>{p.name}{p.isOffboarded ? " (Former)" : ""}</option>)}</select></label>}
          <button type="button" onClick={exportCsv} disabled={!rows.length || displayedKey !== reportKey || loading} className="col-span-2 rounded border border-blue-700 px-3 py-2 text-sm font-medium text-blue-700 disabled:opacity-50 sm:col-span-1">Export CSV</button>
        </section>
        {loading ? <FetchLoadingState message="Loading reports..." fullScreen className="bg-slate-50" /> : report && <><section className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4" aria-label="Report totals">{metrics.map(([key, value]) => <div key={key} className="min-w-0 rounded-xl border bg-white p-3 shadow-sm sm:p-5"><div className="break-words text-xs text-slate-500 sm:text-sm">{label(key)}</div><div className="mt-2 break-all text-lg font-semibold sm:text-2xl">{display(key, value)}</div></div>)}</section>
          {reportView === "charts" ? <ReportCharts report={report} /> : <>
            {rows.length > 0 && <section className="max-w-full min-w-0 overflow-x-auto rounded-xl border bg-white shadow-sm"><div className="flex flex-wrap items-center justify-between gap-3 p-3 sm:p-5"><h2 className="text-lg font-semibold">People ({rows.length})</h2>{pageCount > 1 && <div className="flex items-center gap-2 text-sm"><button type="button" disabled={page === 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-40">Previous</button><span>Page {page} of {pageCount}</span><button type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-40">Next</button></div>}</div><table className="w-full text-left text-sm"><thead className="bg-slate-100"><tr>{columns.map((key) => <th className="whitespace-nowrap p-3" key={key}>{label(key)}</th>)}</tr></thead><tbody>{visibleRows.map((row, i) => <tr key={row.id || i} className="border-t">{columns.map((key) => <td className="whitespace-nowrap p-3" key={key}>{display(key, row[key])}</td>)}</tr>)}</tbody></table></section>}
            {!rows.length && <p className="text-sm text-slate-500">No people match this selection.</p>}
          </>}
          {report.definitions && <section className="rounded-xl border bg-white p-5 text-xs text-slate-500"><h2 className="mb-2 text-sm font-semibold text-slate-700">How these figures are calculated</h2>{Object.entries(report.definitions).map(([key, value]) => <p key={key} className="mb-1">{value}</p>)}</section>}</>}
      </>)}
      {error && <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"><span>{Array.isArray(error) ? error.join(", ") : error}</span>{!intelligence && !recommendations && from <= to && <button type="button" onClick={() => setRetry((value) => value + 1)} className="rounded border border-red-400 px-3 py-1 font-medium hover:bg-red-100">Retry</button>}</div>}
    </div>
  </main>;
}
